'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useCart, useCartToRender } from './cart-context';
import contentData from '@/lib/constants/content';
import SliceZone from '@/components/SliceZone';
import { useRouter, usePathname } from 'next/navigation';
import Cookies from 'js-cookie';
import CartLineItem from './CartLineItem';
import CartPageSticky from './CartPageSticky';
import { elevarBaseData, elevarViewCart } from '@/lib/elevar/cart-events';
import TrueMedWidget from '@/utils/truemed';
import clsx from 'clsx';
import { calculatePrices, totalsDisplayPrice } from './cart-price-utils';
import {
  CartSummarySection,
  SummaryRow,
  checkoutButtonClassName,
  financeMsgClassName,
  orderTotalRowClassName
} from './cart-summary-parts';

export default function CartSummary({ cartFromServer, block, position }) {
  const router = useRouter();
  const cart = useCartToRender(cartFromServer);
  const { cartReady } = useCart();
  const elevarViewCartRan = useRef(false);
  const pathname = usePathname();
  const lastFiredPath = useRef(null);

  const {
    empty_cart_button_text,
    empty_cart_link,
    _metadata,
    modular_blocks,
    disclaimer
  } = block.cart_summary;
  const [discountCode, setDiscountCode] = useState(null);
  const [checkoutButtonRef, checkoutButtonInView, checkoutButtonEntry] =
    useInView({ threshold: 0 });
  // Check if Truemed is eligible based on cart lines
  const truemedEligible = checkTruemedEligible(cart?.lines);
  // Set up initial state for savings and subtotal
  const priceInfo = calculatePrices(cart);
  const savingsPrice = priceInfo.savings;
  const subTotal = priceInfo.subtotal;

  useEffect(() => {
    // Check cookies for discount code
    if (Cookies.get('lsa-cupc')) {
      setDiscountCode(Cookies.get('lsa-cupc'));
    }
  }, [cart]);

  useEffect(() => {
    if (!cartReady || lastFiredPath.current === pathname) return;
    lastFiredPath.current = pathname;

    (async () => {
      await new Promise((resolve) => {
        if (window.ElevarDataLayer) return resolve(true);
        const checkInterval = setInterval(() => {
          if (window.ElevarDataLayer) {
            clearInterval(checkInterval);
            resolve(true);
          }
        }, 100);
      });

      if (window.ElevarDataLayer?.length > 1) {
        await window.ElevarInvalidateContext?.();
      }

      await elevarBaseData(cart);
      elevarViewCart(cart);
    })();
  }, [cartReady, cart]);

  // reset when the user leaves, so a return visit fires again
  useEffect(() => {
    return () => {
      lastFiredPath.current = null;
    };
  }, [pathname]);

  const checkoutButtonAboveViewport =
    checkoutButtonEntry &&
    !checkoutButtonInView &&
    checkoutButtonEntry.boundingClientRect.top < 0;

  // If cart is empty, display a message
  if (!cart || cart?.totalQuantity < 1) {
    return (
      <section className="bg-dawn-grey-10 flex w-full flex-col items-center px-5 py-[160px]">
        <h1 className="h4 px-10 text-center">
          {contentData.cart?.emptyTextLine1['en-us']}
        </h1>
        <p className="h4 px-10 text-center">
          {contentData.cart?.emptyTextLine2['en-us']}
        </p>
        <Link href={empty_cart_link[0].url} prefetch={false}>
          <button className="btn btn-primary mt-6">
            {empty_cart_button_text}
          </button>
        </Link>
      </section>
    );
  }

  return (
    <CartSummarySection
      id={`${_metadata.uid}_${position}`}
      data-cy="cart-summary"
    >
      <div>
        {/* Column widths are duplicated in CartSummarySkeleton — change both. */}
        <div className="flex flex-col items-center lg:flex-row lg:items-start lg:justify-center">
          <div className="w-full pb-10 lg:mr-10 lg:max-w-[610px] lg:pb-0">
            <h1 className="h4 pb-1">Your Cart</h1>
            <div className="body-r text-parlor-green-80 pb-5">
              Free shipping on all US orders
            </div>
            <div>
              <ul>
                {cart?.lines?.map((item, i) => (
                  <CartLineItem key={i} item={item} position={position} />
                ))}
              </ul>
            </div>

            <div className="body-r border-dawn-grey mt-6 w-full border-t pt-4 text-right">
              Subtotal ({cart?.totalQuantity}&nbsp;
              {cart?.totalQuantity > 1 ? 'items' : 'item'}): $
              {totalsDisplayPrice(
                savingsPrice ? subTotal + savingsPrice : subTotal
              )}
            </div>
            {disclaimer?.show_disclaimer &&
              disclaimer?.placement == 'Left Column' && (
                <div
                  dangerouslySetInnerHTML={{
                    __html: disclaimer?.copy
                  }}
                  className="text-dark-grey-text body-xs rte-simple mt-8 hidden lg:block"
                ></div>
              )}
          </div>

          <div className="w-full lg:w-[414px]">
            <div className="h4 pb-4">Order Summary</div>
            <div className="bg-white p-8">
              <SummaryRow
                label="Subtotal"
                labelClassName=""
                className="font-lato"
              >
                <div>
                  <span className="price-r text-black!">
                    $
                    {totalsDisplayPrice(
                      savingsPrice ? subTotal + savingsPrice : subTotal
                    )}
                  </span>
                </div>
              </SummaryRow>
              <SummaryRow label="Your Savings">
                <div className="price-r">
                  - ${totalsDisplayPrice(savingsPrice)}
                </div>
              </SummaryRow>
              <SummaryRow label="Shipping">
                <div className="price-r text-black!">FREE</div>
              </SummaryRow>
              <SummaryRow label="Taxes">
                <div className="body-s text-grey-text">Applied at checkout</div>
              </SummaryRow>
              <SummaryRow label="Promo Code">
                <div className="body-s text-grey-text">Apply at checkout</div>
              </SummaryRow>
              <SummaryRow
                label="Order Total"
                labelClassName="body-l"
                className={orderTotalRowClassName}
              >
                <div className="sh10 text-[#CC4D0F]">
                  ${totalsDisplayPrice(subTotal)}
                </div>
              </SummaryRow>
              {cart?.cost.subtotalAmount.amount >= 200 && (
                <CartFinanceMsg
                  id="main"
                  subTotal={cart?.cost.subtotalAmount.amount}
                  styleClass={financeMsgClassName}
                />
              )}
              <button
                id="checkout-button"
                data-cy="checkout-button"
                ref={checkoutButtonRef}
                className={checkoutButtonClassName}
                onClick={async () => {
                  const checkoutUrl = discountCode
                    ? `${cart?.checkoutUrl}&discount=${discountCode}`
                    : cart?.checkoutUrl;
                  router.push(checkoutUrl);
                }}
              >
                <span>Checkout</span>
              </button>
              <TrueMedWidget checkout={true} eligible={truemedEligible} />
            </div>
            <SliceZone blocks={modular_blocks} />
          </div>
        </div>
        {disclaimer?.show_disclaimer && (
          <div
            dangerouslySetInnerHTML={{
              __html: disclaimer?.copy
            }}
            className={clsx(
              `text-dark-grey-text body-xs rte-simple mx-auto mt-5 max-w-170 lg:mt-8 lg:flex`,
              { 'lg:hidden': disclaimer?.placement == 'Left Column' }
            )}
          ></div>
        )}
      </div>
      {cart?.totalQuantity > 0 && (
        <CartPageSticky
          cartData={cart}
          checkoutButtonInView={checkoutButtonInView}
          checkoutButtonAboveViewport={checkoutButtonAboveViewport}
          subTotal={subTotal}
          totalsDisplayPrice={totalsDisplayPrice}
          CartFinanceMsg={CartFinanceMsg}
        />
      )}
    </CartSummarySection>
  );
}

function useInView({ threshold } = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  const [entry, setEntry] = useState(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new IntersectionObserver(
      ([e]) => {
        setInView(e.isIntersecting);
        setEntry(e);
      },
      { threshold }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, inView, entry];
}

export const CartFinanceMsg = ({ subTotal, styleClass, id }) => {
  const [developDomain, setDevelopDomain] = useState(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      const isDevDomain = ['beta', 'alpha', 'localhost', 'vercel'].some((env) =>
        hostname.includes(env)
      );
      setDevelopDomain(isDevDomain);
    }
  }, []);

  useEffect(() => {
    if (developDomain === null || typeof window['affirm'] !== 'undefined')
      return;

    const affirmApiKey = developDomain
      ? 'FWUPRAEPEJ8CDZUV'
      : '2LSY5K7PY0C2IZQ7';
    const affirmScript = document.createElement('script');

    const affirmUrl = developDomain
      ? 'https://cdn1-sandbox.affirm.com/js/v2/affirm.js'
      : 'https://cdn1.affirm.com/js/v2/affirm.js';

    affirmScript.src = affirmUrl;

    affirmScript.onload = () => {
      window._affirm_config = {
        public_api_key: affirmApiKey,
        script: `${affirmScript}`,
        locale: 'en_US',
        country_code: 'USA'
      };
    };
    document.head.appendChild(affirmScript);

    return () => {
      if (affirmScript.parentNode) {
        affirmScript.parentNode.removeChild(affirmScript);
      }
    };
  }, [developDomain]);

  useEffect(() => {
    if (typeof window['affirm'] !== 'undefined') {
      window.affirm.ui.ready(() => {
        window.affirm.ui.refresh();
      });
    }
  }, [subTotal]);

  return (
    subTotal && (
      <div
        id={`finance-msg-subtotal-${id}`}
        className={styleClass}
        data-page-type="cart"
        data-affirm-color="black"
        data-amount={Math.round(subTotal * 100)}
        aria-label="See if you qualify (opens in modal)"
      />
    )
  );
};

function checkTruemedEligible(lines) {
  if (!lines) return false;

  return lines.some((line) => {
    const truemedEligible = line.merchandise.product.truemedEligible; // value true or false
    return truemedEligible;
  });
}
